import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { Clock, ShieldCheck, MessageSquareWarning, Upload, X } from "lucide-react";

/**
 * Iter 68 — Player-facing pending-contract review widget.
 *
 * Reads `/api/contracts/mine` (server-side isolation, account name masked).
 * For each contract in `pending_player_review` we show a live 72h countdown
 * and two forms:
 *   • Confirm — requires the exact account name, at least one R2 screenshot
 *     upload, and the player must RETYPE the streamer's affiliate code (the
 *     backend compares case-insensitively w/ whitespace trimmed and blocks
 *     the confirmation on mismatch).
 *   • Request changes — free text; backend sanitises & emails the streamer.
 *
 * `changes_requested` contracts stay listed too so the player sees they've
 * pinged the streamer and are waiting for a resend.
 */
function fmtCountdown(deadlineIso) {
  if (!deadlineIso) return { text: "—", tone: "default", expired: false };
  const now = Date.now();
  const dl = new Date(deadlineIso).getTime();
  const diff = dl - now;
  if (diff <= 0) return { text: "Window expired", tone: "danger", expired: true };
  const totalMin = Math.floor(diff / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const s = Math.floor((diff % 60000) / 1000);
  const tone = h < 12 ? "danger" : h < 24 ? "warn" : "ok";
  return { text: `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s left`, tone, expired: false };
}

function Countdown({ deadlineIso, testId }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const c = useMemo(() => fmtCountdown(deadlineIso), [deadlineIso]);
  const tones = {
    ok: "text-[#2DD4A7] border-[#2DD4A7]/50",
    warn: "text-[#F5B841] border-[#F5B841]/50",
    danger: "text-[#F26D6D] border-[#F26D6D]/60",
    default: "text-[#8B97AE] border-white/20",
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest tabular-nums ${tones[c.tone]}`}>
      <Clock className="w-3 h-3" /> {c.text}
    </span>
  );
}

function ConfirmForm({ contract, onDone }) {
  const [accountName, setAccountName] = useState("");
  const [affiliateTyped, setAffiliateTyped] = useState("");
  const [proofs, setProofs] = useState([]); // [{path, filename}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);

  const upload = useCallback(async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    const CAPS = {
      "image/jpeg": 10, "image/png": 10,
      "application/pdf": 25,
      "video/mp4": 50, "video/quicktime": 50, "video/webm": 50,
    };
    const cap = CAPS[file.type || ""];
    if (!cap) { toast.error("Unsupported file type. Accepted: JPG, PNG, PDF, MP4, MOV, WEBM."); return; }
    if (file.size > cap * 1024 * 1024) { toast.error(`File too large (max ${cap} MB).`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/players/me/proofs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofs((prev) => [...prev, { path: data.path, filename: data.filename || file.name }]);
      setFile(null);
      toast.success("Screenshot attached.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setUploading(false); }
  }, [file]);

  const removeProof = (path) => setProofs((prev) => prev.filter((p) => p.path !== path));

  const submit = async () => {
    if (accountName.trim().length < 1) { toast.error("Enter the account name you registered with."); return; }
    if (affiliateTyped.trim().length < 1) { toast.error("Retype the streamer's affiliate code."); return; }
    if (proofs.length < 1) { toast.error("Attach at least one screenshot."); return; }
    setSubmitting(true);
    try {
      await api.post(`/contracts/${contract.id}/confirm`, {
        accountName: accountName.trim(),
        proofFileKeys: proofs.map((p) => p.path),
        affiliateCodeTyped: affiliateTyped.trim(),
      });
      toast.success("Contract confirmed — awaiting admin verification.");
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message) || apiErrorMessage(e);
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border border-[#2DD4A7]/30 bg-[#2DD4A7]/[0.03] p-4 mt-3" data-testid={`pc-confirm-${contract.id}`}>
      <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-[#2DD4A7]" /><h4 className="font-display font-bold">Confirm this contract</h4></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Account name (as registered)</label>
          <input
            data-testid={`pc-account-name-${contract.id}`}
            value={accountName}
            onChange={(e) => setAccountName(e.target.value.slice(0, 120))}
            className="w-full rounded-none bg-transparent border border-white/20 h-10 text-sm text-[#E8ECF4] px-3"
            placeholder="e.g. dune_walker92"
            maxLength={120}
          />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Retype affiliate code you signed up with</label>
          <input
            data-testid={`pc-affiliate-typed-${contract.id}`}
            value={affiliateTyped}
            onChange={(e) => setAffiliateTyped(e.target.value.slice(0, 64))}
            className="w-full rounded-none bg-transparent border border-white/20 h-10 text-sm text-[#E8ECF4] px-3 font-mono uppercase tracking-widest"
            placeholder="e.g. BIGWIN2024"
            maxLength={64}
            autoComplete="off"
          />
          <div className="text-[10px] text-[#8B97AE] mt-1">Case-insensitive. Must match the code on this contract.</div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Proof screenshots (at least one)</label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            data-testid={`pc-file-input-${contract.id}`}
            type="file"
            accept="image/jpeg,image/png,application/pdf,video/mp4,video/quicktime,video/webm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block text-xs text-[#8B97AE] file:mr-3 file:border file:border-white/20 file:bg-transparent file:text-white file:px-3 file:py-1.5 file:font-mono file:text-[10px] hover:file:bg-white/10"
          />
          <button
            data-testid={`pc-file-upload-${contract.id}`}
            onClick={upload}
            disabled={uploading || !file}
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-[#2DD4A7] text-[#2DD4A7] font-mono text-[10px] uppercase tracking-widest hover:bg-[#2DD4A7] hover:text-[#04150F] transition-colors disabled:opacity-40"
          >
            <Upload className="w-3 h-3" />{uploading ? "Uploading…" : "Attach"}
          </button>
        </div>
        {proofs.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid={`pc-proofs-list-${contract.id}`}>
            {proofs.map((p) => (
              <li key={p.path} className="flex items-center gap-2 text-[11px] font-mono text-[#E8ECF4]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A7] inline-block" />
                <span className="truncate max-w-[380px]">{p.filename}</span>
                <button onClick={() => removeProof(p.path)} className="text-[#F26D6D] hover:text-[#F5B841]" data-testid={`pc-proof-remove-${p.path}`}><X className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          data-testid={`pc-confirm-submit-${contract.id}`}
          onClick={submit}
          disabled={submitting}
          className="px-4 h-10 bg-[#2DD4A7] text-[#04150F] font-mono text-xs uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"
        >{submitting ? "Submitting…" : "Confirm contract"}</button>
      </div>
    </div>
  );
}

function RequestChangesForm({ contract, onDone }) {
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (msg.trim().length < 2) { toast.error("Explain what needs to change."); return; }
    setSubmitting(true);
    try {
      await api.post(`/contracts/${contract.id}/request-changes`, { message: msg.trim() });
      toast.success("Change request sent to the streamer.");
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const errMsg = (d && typeof d === "object" && d.message) || apiErrorMessage(e);
      toast.error(errMsg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border border-[#F5B841]/30 bg-[#F5B841]/[0.03] p-4 mt-3" data-testid={`pc-changes-${contract.id}`}>
      <div className="flex items-center gap-2 mb-3"><MessageSquareWarning className="w-4 h-4 text-[#F5B841]" /><h4 className="font-display font-bold">Request changes</h4></div>
      <textarea
        data-testid={`pc-changes-message-${contract.id}`}
        rows={4}
        maxLength={2000}
        value={msg}
        onChange={(e) => setMsg(e.target.value.slice(0, 2000))}
        placeholder="Tell the streamer what to fix — wager amount, affiliate code, operator, etc."
        className="w-full rounded-none bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-3 resize-none"
      />
      <div className="mt-3 flex justify-end">
        <button
          data-testid={`pc-changes-submit-${contract.id}`}
          onClick={submit}
          disabled={submitting || msg.trim().length < 2}
          className="px-4 h-10 bg-[#F5B841] text-[#04150F] font-mono text-xs uppercase tracking-widest hover:bg-[#F26D6D] hover:text-white transition-colors disabled:opacity-40"
        >{submitting ? "Sending…" : "Send request"}</button>
      </div>
    </div>
  );
}

function ContractRow({ c, streamerName, onChanged }) {
  const [mode, setMode] = useState(null); // "confirm" | "changes" | null
  const isPending = c.status === "pending_player_review";
  const isChangesRequested = c.status === "changes_requested";
  return (
    <div className="border border-[#F5B841]/30 bg-[#F5B841]/[0.04] p-4 mb-3" data-testid={`pending-contract-${c.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-display font-bold text-[#E8ECF4]">{streamerName || "Streamer"}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{c.vertical} · code <span className="text-[#E8ECF4]">{c.affiliateCode}</span></span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid={`pending-contract-status-${c.id}`}>{c.status.replace(/_/g, " ")}</span>
        {isPending && <Countdown deadlineIso={c.playerReviewDeadline} testId={`pending-contract-countdown-${c.id}`} />}
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Agreed wager</div><div className="tabular-nums font-mono text-[#E8ECF4]">${Number(c.agreedWagerAmount || 0).toLocaleString()}</div></div>
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Duration</div><div className="tabular-nums font-mono text-[#E8ECF4]">{c.contractDurationMonths || 3} months</div></div>
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Floor min</div><div className="tabular-nums font-mono text-[#E8ECF4]">${Number(c.floorMinimum || 0).toLocaleString()}</div></div>
      </div>

      {isChangesRequested && (
        <div className="mt-3 border-l-2 border-[#F5B841] bg-[#F5B841]/[0.06] px-3 py-2 text-xs text-[#E8ECF4]" data-testid={`pending-contract-changes-note-${c.id}`}>
          You asked for changes. Waiting for the streamer to resend with updated terms.
        </div>
      )}

      {isPending && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            data-testid={`pending-contract-btn-confirm-${c.id}`}
            onClick={() => setMode(mode === "confirm" ? null : "confirm")}
            className={`px-3 h-9 border font-mono text-[10px] uppercase tracking-widest transition-colors ${mode === "confirm" ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-[#2DD4A7]/60 text-[#2DD4A7] hover:bg-[#2DD4A7]/10"}`}
          >Confirm</button>
          <button
            data-testid={`pending-contract-btn-changes-${c.id}`}
            onClick={() => setMode(mode === "changes" ? null : "changes")}
            className={`px-3 h-9 border font-mono text-[10px] uppercase tracking-widest transition-colors ${mode === "changes" ? "bg-[#F5B841] text-[#04150F] border-[#F5B841]" : "border-[#F5B841]/60 text-[#F5B841] hover:bg-[#F5B841]/10"}`}
          >Request changes</button>
        </div>
      )}

      {mode === "confirm" && <ConfirmForm contract={c} onDone={() => { setMode(null); onChanged && onChanged(); }} />}
      {mode === "changes" && <RequestChangesForm contract={c} onDone={() => { setMode(null); onChanged && onChanged(); }} />}
    </div>
  );
}

export default function PendingContractReview() {
  const [rows, setRows] = useState([]);
  const [streamerNames, setStreamerNames] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/mine");
      setRows(Array.isArray(data) ? data : []);
      const sids = Array.from(new Set((data || []).map((c) => c.streamerId).filter(Boolean)));
      const names = {};
      await Promise.all(sids.map(async (sid) => {
        try {
          const r = await api.get(`/streamers/${sid}`);
          names[sid] = r.data?.displayName || r.data?.slug || "Streamer";
        } catch {
          names[sid] = "Streamer";
        }
      }));
      setStreamerNames(names);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = rows.filter((c) => c.status === "pending_player_review" || c.status === "changes_requested");
  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <section className="lg:col-span-12 border border-[#F5B841]/40 bg-[#F5B841]/[0.05] p-6 lg:p-8" data-testid="pending-contracts-section">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-4 h-4 text-[#F5B841]" /><h2 className="font-display font-bold text-lg">Contracts awaiting your review</h2></div>
      <p className="text-xs text-[#8B97AE] mb-4">You have {pending.length} contract{pending.length === 1 ? "" : "s"} pending. Confirm within the 72-hour window — otherwise the streamer's reserved credits are released and you'll need a new offer.</p>
      {pending.map((c) => (
        <ContractRow key={c.id} c={c} streamerName={streamerNames[c.streamerId]} onChanged={load} />
      ))}
    </section>
  );
}
